/* eslint-disable unicorn/filename-case -- kebab-case per docs/conventions/core.md: this file
   exports a hook, not a React component, so the PascalCase-for-components rule does not apply. */
import * as React from 'react';

import { readCachedChildren } from './read-cached-children.js';

/**
 * Forgets a parent's outstanding onExpandRow record when the consumer drops its cached children.
 *
 * A consumer that reloads its data and resets `childRowsByParentId` turns a cached parent back into
 * "not yet fetched". Nothing else releases the record: the cached half of `shouldReleaseRequest`
 * only fires while the entry is still present, and the consumer half is inert for anyone who never
 * wired `loadingParentIds`. Without this the row is unretryable for the component's whole lifetime.
 */
export function useReleaseResetRequests<TChild>(
  childRowsByParentId: Readonly<Record<string, TChild[] | undefined>>,
  releaseUncachedRequests: (stillCached: (parentId: string) => boolean) => void,
): void {
  const previousChildRowsByParentId = React.useRef(childRowsByParentId);

  React.useLayoutEffect(() => {
    const previous = previousChildRowsByParentId.current;
    previousChildRowsByParentId.current = childRowsByParentId;
    if (previous === childRowsByParentId) return;
    // Only a parent that WAS cached and no longer is: an in-flight request has no entry either, and
    // releasing that one would re-fire onExpandRow for a fetch already on its way.
    releaseUncachedRequests(
      (parentId) =>
        readCachedChildren(previous, parentId) === undefined ||
        readCachedChildren(childRowsByParentId, parentId) !== undefined,
    );
  }, [childRowsByParentId, releaseUncachedRequests]);
}
